import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { useNavigate, useParams } from 'react-router-dom';
import * as pdfjsLib from 'pdfjs-dist';

// Set up PDF.js worker - use local file from public folder
pdfjsLib.GlobalWorkerOptions.workerSrc = '/pdf.worker.min.js';

// Global PDF rendering queue to prevent conflicts
let pdfRenderQueue = [];
let isRendering = false;

const renderPdfPage = async (url, scale = 1.0) => {
    return new Promise((resolve, reject) => {
        pdfRenderQueue.push({ url, scale, resolve, reject });
        processQueue();
    });
};

const processQueue = async () => {
    if (isRendering || pdfRenderQueue.length === 0) return;
    
    isRendering = true;
    const { url, scale, resolve, reject } = pdfRenderQueue.shift();
    
    try {
        const pdf = await pdfjsLib.getDocument(url).promise;
        const page = await pdf.getPage(1);
        
        const viewport = page.getViewport({ scale });
        const canvas = document.createElement('canvas');
        const context = canvas.getContext('2d');
        canvas.width = viewport.width;
        canvas.height = viewport.height;

        await page.render({
            canvasContext: context,
            viewport: viewport
        }).promise;

        resolve(canvas.toDataURL('image/png'));
    } catch (error) {
        reject(error);
    } finally {
        isRendering = false;
        // Process next item in queue
        setTimeout(processQueue, 100);
    }
};

const ThesisDetailView = ({ currentUser }) => {
    const { department, major, year } = useParams();
    const navigate = useNavigate();
    const [theses, setTheses] = useState([]);
    const [filteredTheses, setFilteredTheses] = useState([]);
    const [keyword, setKeyword] = useState('');  
    const [selectedThesis, setSelectedThesis] = useState(null);
    const [viewPdf, setViewPdf] = useState(false);
    const [sortBy, setSortBy] = useState('latest');
    const [lastUpdated, setLastUpdated] = useState('');
    const [departments, setDepartments] = useState([]);

    const [showGeneralRequestForm, setShowGeneralRequestForm] = useState(false);
    const [requestDeptFilter, setRequestDeptFilter] = useState('');
    const [requestMajorId, setRequestMajorId] = useState('');
    const [requestThesisId, setRequestThesisId] = useState('');
    const [requestSearch, setRequestSearch] = useState('');
    const [requestPickerOpen, setRequestPickerOpen] = useState(false);
    const [showRequestForm, setShowRequestForm] = useState(false);
    const [requestType, setRequestType] = useState('admin'); // 'admin' or 'author'
    const [requestData, setRequestData] = useState({ name: '', email: '', message: '' });
    const [isSubmitting, setIsSubmitting] = useState(false);

    const [selectedFileUrl, setSelectedFileUrl] = useState('');

    const getPreviewUrl = async (filePath) => {
        if (!filePath) return '';
        if (/^https?:\/\//i.test(filePath)) return filePath;
        try {
            const res = await axios.get('/api/files/preview', {
                params: { path: filePath }
            });
            return res.data?.url || '';
        } catch {
            return '';
        }
    };

    const getSignedUrl = async (filePath, { silent = false } = {}) => {
        if (!filePath) return '';
        if (/^https?:\/\//i.test(filePath)) return filePath;
        if (!currentUser?.user_id) return '';
        try {
            const res = await axios.get('/api/files/signed', {
                params: { path: filePath, userId: currentUser.user_id }
            });
            return res.data?.url || '';
        } catch (err) {
            if (!silent) {
                alert(err.response?.data?.error || 'Unable to access this file.');
            }
            return '';
        }
    };

    const getFilenameFromPath = (filePath) => {
        if (!filePath) return 'thesis.pdf';
        const withoutQuery = filePath.split('?')[0];
        const name = withoutQuery.split('/').pop() || 'thesis.pdf';
        return name.toLowerCase().endsWith('.pdf') ? name : `${name}.pdf`;
    };

    const getAuthorData = (thesis) => {
        if (!thesis?.author_data) return [];
        if (Array.isArray(thesis.author_data)) return thesis.author_data;
        try {
            return JSON.parse(thesis.author_data);
        } catch {
            return [];
        }
    };

    // Helper: Convert full name string to {firstName, lastName} object
    const parseAuthorName = (fullName) => {
        if (!fullName) return { firstName: '', lastName: '' };
        
        let cleanName = fullName.trim();
        
        // Check if name uses the new delimiter format (firstName||lastName)
        if (cleanName.includes('||')) {
          let [firstName, lastName] = cleanName.split('||');
          firstName = (firstName || '').trim().replace(/,\s*$/, '');
          lastName = (lastName || '').trim().replace(/,\s*$/, '');
          return { firstName, lastName };
        }
        
        // Check if name is in citation format "LastName, FirstName"
        if (cleanName.includes(',')) {
          const [last, first] = cleanName.split(',').map(s => s.trim());
          return { firstName: (first || '').trim(), lastName: (last || '').trim() };
        }
        
        // Fallback for old format (space-separated)
        const parts = cleanName.split(/\s+/);
        if (parts.length === 0) return { firstName: '', lastName: '' };
        if (parts.length === 1) return { firstName: '', lastName: parts[0] };
        const lastName = parts[parts.length - 1];
        const firstName = parts.slice(0, -1).join(' ');
        return { firstName, lastName };
    };

    // Helper function to format authors in citation format (LastName, FirstName)
    const formatAuthorsForCitation = (authors) => {
        if (!Array.isArray(authors) || authors.length === 0) return '';
        return authors
            .map(a => {
                let first = '';
                let last = '';
                if (typeof a === 'string') {
                    // Convert string format
                    const parsed = parseAuthorName(a);
                    first = parsed.firstName;
                    last = parsed.lastName;
                } else if (typeof a === 'object') {
                    // Already object format
                    first = a.firstName || '';
                    last = a.lastName || '';
                }
                return last ? `${last}, ${first}`.trim() : first;
            })
            .filter(name => name)
            .join('; ');
    };

    useEffect(() => {
        fetchTheses();
    }, [department, major, year]);

    useEffect(() => {
        filterTheses();
    }, [keyword, sortBy, theses]);

    useEffect(() => {
        setRequestMajorId('');
    }, [requestDeptFilter]);

    // Generate APA-style citation
    const formatCitation = (thesis) => {
        const authors = formatAuthorsForCitation(thesis.authors);
        const displayAuthors = authors || 'Unknown Author';
        const year = thesis.year || 'N.D.';
        const title = thesis.title || 'Untitled';
        const department = thesis.department || 'Unknown Department';
        
        return `${displayAuthors} (${year}). ${title}. ${department}.`;
    };

    const canViewFullPdfForThesis = (thesis) => {
        if (!thesis?.file_path) return false;
        return Boolean(thesis && currentUser);
    };

    // Render thumbnail preview of PDF for cards
    const PdfPreviewThumbnail = ({ filePath }) => {
        const [imageData, setImageData] = useState(null);
        const [loading, setLoading] = useState(false);
        const [error, setError] = useState(false);
        const [isVisible, setIsVisible] = useState(false);
        const thumbnailRef = useRef(null);

        useEffect(() => {
            const observer = new IntersectionObserver(
                ([entry]) => {
                    if (entry.isIntersecting) {
                        setIsVisible(true);
                    }
                },
                { threshold: 0.1 }
            );

            if (thumbnailRef.current) {
                observer.observe(thumbnailRef.current);
            }

            return () => {
                if (thumbnailRef.current) {
                    observer.unobserve(thumbnailRef.current);
                }
            };
        }, []);

        useEffect(() => {
            if (isVisible && !imageData && !loading && !error) {
                renderThumbnail();
            }
        }, [isVisible, imageData, loading, error]);

        const renderThumbnail = async () => {
            if (!filePath) return;
            
            setLoading(true);
            setError(false);
            
            try {
                const url = await getPreviewUrl(filePath);
                if (!url) {
                    setError(true);
                    setLoading(false);
                    return;
                }
                const dataUrl = await renderPdfPage(url, 0.8);
                setImageData(dataUrl);
            } catch (err) {
                console.error('Error rendering PDF thumbnail:', err);
                setError(true);
            } finally {
                setLoading(false);
            }
        };

        if (!filePath) {
            return <div style={thumbnailErrorStyle}>PDF file unavailable</div>;
        }

        if (!isVisible) {
            return <div ref={thumbnailRef} style={thumbnailLoadingStyle}>📄 Scroll to load...</div>;
        }

        if (loading) {
            return <div style={thumbnailLoadingStyle}>📄 Loading...</div>;
        }

        if (error || !imageData) {
            return <div style={thumbnailErrorStyle}>PDF preview unavailable</div>;
        }

        return (
            <img src={imageData} alt="PDF Thumbnail" style={{ width: '100%', height: 'auto', borderRadius: '4px' }} />
        );
    };

    const fetchTheses = async () => {
        try {
            const url = `/api/theses/search?department=${department}&year=${year}`;
            let res = await axios.get(url);

            const deptRes = await axios.get('/api/departments');
            setDepartments(deptRes.data || []);

            const deptMap = {};
            deptRes.data.forEach(dept => {
                (dept.degrees || []).forEach(degree => {
                    (degree.majors || []).forEach(m => {
                        deptMap[m.major_id] = { major_name: m.major_name, degree_name: degree.degree_name };
                    });
                });
            });

            if (major) {
                res.data = res.data.filter(thesis => {
                    if (major === 'General') {
                        return !thesis.major_id;
                    }
                    return deptMap[thesis.major_id]?.major_name === decodeURIComponent(major);
                });
            }

            setTheses(res.data);

            const lastUpRes = await axios.get('/api/logs?limit=1');
            if (lastUpRes.data[0]) {
                setLastUpdated(new Date(lastUpRes.data[0].created_at).toLocaleString());
            }
        } catch (error) {
            console.error("Failed to fetch theses:", error);
        }
    };

    const filterTheses = () => {
        let results = theses.filter(t => 
            t.title.toLowerCase().includes(keyword.toLowerCase()) ||
            t.authors?.some(a => a.toLowerCase().includes(keyword.toLowerCase())) ||
            t.keywords?.some(k => k.toLowerCase().includes(keyword.toLowerCase()))
        );

        // Sort
        if (sortBy === 'title_asc') results.sort((a, b) => a.title.localeCompare(b.title));
        else if (sortBy === 'title_desc') results.sort((a, b) => b.title.localeCompare(a.title));
        else if (sortBy === 'year_desc') results.sort((a, b) => b.year - a.year);
        else if (sortBy === 'year_asc') results.sort((a, b) => a.year - b.year);

        setFilteredTheses(results);
    };

    const handleDownload = async (filePath) => {
        if (!filePath) {
            alert('PDF file unavailable.');
            return;
        }
        const url = await getSignedUrl(filePath);
        if (!url) return;
        try {
            const response = await axios.get(url, { responseType: 'blob' });
            const blobUrl = window.URL.createObjectURL(response.data);
            const link = document.createElement('a');
            link.href = blobUrl;
            link.download = getFilenameFromPath(filePath);
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(blobUrl);
        } catch (err) {
            alert('Failed to download file. Please try again.');
        }
    };

    useEffect(() => {
        let active = true;
        const loadUrl = async () => {
            if (!selectedThesis?.file_path) {
                setSelectedFileUrl('');
                return;
            }
            if (!canViewFullPdfForThesis(selectedThesis)) {
                setSelectedFileUrl('');
                return;
            }
            const url = await getSignedUrl(selectedThesis.file_path, { silent: true });
            if (active) setSelectedFileUrl(url);
        };
        loadUrl();
        return () => { active = false; };
    }, [selectedThesis?.file_path, currentUser?.user_id, currentUser?.role, currentUser?.email]);

    const submitRequest = async () => {
        if (!requestData.name || !requestData.email) {
            alert("Please provide your name and email.");
            return;
        }

        setIsSubmitting(true);
        const payload = {
            ...requestData,
            thesis_id: selectedThesis?.thesis_id || null,
            thesis_title: selectedThesis?.title || "Thesis Request",
            type: 'specific'
        };

        try {
            await axios.post('/api/requests', payload);
            alert("Request sent successfully! Our staff will contact you via email.");
            setShowRequestForm(false);
            setRequestData({ name: '', email: '', message: '' });
        } catch (err) {
            alert("Failed to send request. Please check your connection.");
        } finally {
            setIsSubmitting(false);
        }
    };

    const submitAuthorRequest = async () => {
        if (!requestData.name || !requestData.email) {
            alert("Please provide your name and email.");
            return;
        }

        // Check if thesis has any authors with emails
        const authorsWithEmails = selectedThesis.author_data?.filter(a => a.email) || [];
        if (authorsWithEmails.length === 0) {
            alert("No authors have provided email addresses for this thesis.");
            return;
        }

        setIsSubmitting(true);
        const payload = {
            thesis_id: selectedThesis?.thesis_id,
            author_ids: authorsWithEmails.map(a => a.author_id),
            requester_name: requestData.name,
            requester_email: requestData.email,
            message: requestData.message
        };

        try {
            await axios.post('/api/author-requests', payload);
            alert("Request sent to authors! They will review and respond via email.");
            setShowRequestForm(false);
            setRequestData({ name: '', email: '', message: '' });
            setRequestType('admin');
        } catch (err) {
            const serverError = err.response?.data?.error;
            const serverDetails = err.response?.data?.details;
            alert(serverDetails ? `${serverError}: ${serverDetails}` : (serverError || "Failed to send request. Please check your connection."));
        } finally {
            setIsSubmitting(false);
        }
    };

    const submitGeneralRequest = async () => {
        if (!requestData.name || !requestData.email) {
            alert("Please provide your name and email.");
            return;
        }

        setIsSubmitting(true);
        const selected = theses.find(t => String(t.thesis_id) === String(requestThesisId));
        const isSpecific = Boolean(selected);
        const payload = {
            ...requestData,
            thesis_id: isSpecific ? selected.thesis_id : null,
            thesis_title: isSpecific ? selected.title : "General Request",
            type: isSpecific ? 'specific' : 'general'
        };

        try {
            await axios.post('/api/requests', payload);
            alert("Request sent successfully! Our staff will contact you via email.");
            setShowGeneralRequestForm(false);
            setRequestDeptFilter('');
            setRequestMajorId('');
            setRequestThesisId('');
            setRequestSearch('');
            setRequestPickerOpen(false);
            setRequestData({ name: '', email: '', message: '' });
        } catch (err) {
            alert("Failed to send request. Please check your connection.");
        } finally {
            setIsSubmitting(false);
        }
    };

    const submitGeneralAuthorRequest = async () => {
        if (!requestData.name || !requestData.email) {
            alert("Please provide your name and email.");
            return;
        }

        const selected = theses.find(t => String(t.thesis_id) === String(requestThesisId));
        if (!selected) {
            alert("Please select a specific thesis to request from authors.");
            return;
        }

        const authorsWithEmails = selected.author_data?.filter(a => a.email) || [];
        if (authorsWithEmails.length === 0) {
            alert("No authors have provided email addresses for this thesis.");
            return;
        }

        setIsSubmitting(true);
        const payload = {
            thesis_id: selected.thesis_id,
            author_ids: authorsWithEmails.map(a => a.author_id),
            requester_name: requestData.name,
            requester_email: requestData.email,
            message: requestData.message
        };

        try {
            await axios.post('/api/author-requests', payload);
            alert("Request sent to authors! They will review and respond via email.");
            setShowGeneralRequestForm(false);
            setRequestDeptFilter('');
            setRequestMajorId('');
            setRequestThesisId('');
            setRequestSearch('');
            setRequestPickerOpen(false);
            setRequestData({ name: '', email: '', message: '' });
        } catch (err) {
            const serverError = err.response?.data?.error;
            const serverDetails = err.response?.data?.details;
            alert(serverDetails ? `${serverError}: ${serverDetails}` : (serverError || "Failed to send request. Please check your connection."));
        } finally {
            setIsSubmitting(false);
        }
    };

    const canRequestAuthors = Boolean(selectedThesis?.author_data?.some(a => a.email));
    const selectedThesisForRequest = theses.find(t => String(t.thesis_id) === String(requestThesisId));
    const canAuthorRequest = Boolean(selectedThesisForRequest && selectedThesisForRequest.author_data?.some(a => a.email));
    const majorsForRequestDept = requestDeptFilter
        ? (departments.find(d => d.dept_name === requestDeptFilter)?.degrees || []).flatMap(d => d.majors || [])
        : [];

    return (
        <div style={{ padding: '20px', maxWidth: '1200px', margin: '0 auto' }}>
            <style>
                {`
                @keyframes spin {
                    0% { transform: rotate(0deg); }
                    100% { transform: rotate(360deg); }
                }
                `}
            </style>
            <button onClick={() => navigate('/gallery')} style={{ ...backBtn, marginBottom: '20px' }}>← Back to Gallery</button>

            <h2 style={{ color: '#333', marginBottom: '10px' }}>
                {department} {major ? `- ${decodeURIComponent(major)}` : ''} - Year {year}
            </h2>
            <p style={{ color: '#666', marginBottom: '20px' }}>Found {filteredTheses.length} thesis(es)</p>

            <div style={{ ...bannerStyle, marginBottom: '20px' }}>
                <div>
                    <h4 style={{ margin: 0, color: '#004085' }}>Want a specific thesis?</h4>
                    <p style={{ margin: 0, color: '#004085', fontSize: '0.95rem' }}>Submit a request and our staff will assist you.</p>
                    {lastUpdated && <p style={{ color: '#004085', fontSize: '0.8rem', marginTop: '6px' }}>Last Collection Update: {lastUpdated}</p>}
                </div>
                <button
                    onClick={() => {
                        setRequestDeptFilter('');
                        setRequestMajorId('');
                        setRequestThesisId('');
                        setRequestSearch('');
                        setRequestPickerOpen(false);
                        setRequestData({ name: '', email: '', message: '' });
                        setShowGeneralRequestForm(true);
                    }}
                    style={{ ...btnStyle, background: '#007bff', fontWeight: 'bold' }}
                >
                    Request PDF File
                </button>
            </div>

            {/* SEARCH & FILTER BAR */}
            <div style={filterBarStyle}>
                <input 
                    type="text" 
                    placeholder="Search by title, author, or keywords..." 
                    value={keyword} 
                    onChange={(e) => setKeyword(e.target.value)} 
                    style={{ ...inputStyle, flex: 2 }} 
                />
                <select value={sortBy} onChange={(e) => setSortBy(e.target.value)} style={inputStyle}>
                    <option value="latest">Latest</option>
                    <option value="title_asc">Title (A-Z)</option>
                    <option value="title_desc">Title (Z-A)</option>
                </select>
            </div>

            {/* THESES LIST */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: '20px' }}>
                {filteredTheses.length > 0 ? (
                    filteredTheses.map((t) => (
                        <div key={t.thesis_id} style={cardStyle}>
                            <h4 style={{ marginBottom: '8px', lineHeight: '1.35', fontSize: '1.25rem' }}>{t.title}</h4>
                            
                            {/* CITATION */}
                            <p style={citationStyle}>{formatCitation(t)}</p>
                            <div style={sectionDividerStyle} />

                            {/* PDF THUMBNAIL PREVIEW */}
                            <div style={thumbnailWrapperStyle}>
                                <PdfPreviewThumbnail filePath={t.file_path} />
                            </div>

                            {t.authors && t.authors.length > 0 && (
                                <p style={{ fontSize: '1rem', color: '#555', marginBottom: '10px' }}>
                                    <strong>Authors:</strong> {formatAuthorsForCitation(t.authors)}
                                </p>
                            )}

                            {t.keywords && t.keywords.length > 0 && (
                                <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap', marginBottom: '10px' }}>
                                    {t.keywords.slice(0, 3).map((k, i) => (
                                        <span key={i} style={{ background: '#eef', padding: '4px 8px', borderRadius: '12px', fontSize: '12px' }}>
                                            {k}
                                        </span>
                                    ))}
                                </div>
                            )}

                            {(t.authors?.length > 0 || t.keywords?.length > 0) && <div style={sectionDividerStyle} />}

                            <p style={{ fontSize: '1rem', color: '#555', lineHeight: '1.6', marginBottom: '14px' }}>
                                {t.abstract?.substring(0, 380)}...
                            </p>

                            <button onClick={() => { 
                                setSelectedThesis(t); 
                                setViewPdf(false); 
                            }} style={viewMoreBtn}>
                                View Details
                            </button>
                        </div>
                    ))
                ) : (
                    <div style={{ gridColumn: '1/-1', textAlign: 'center', padding: '40px', color: '#999' }}>
                        <p>No theses found matching your search.</p>
                    </div>
                )}
            </div>

            {/* THESIS DETAIL MODAL */}
            {selectedThesis && (
                (() => {
                    const hasPdf = Boolean(selectedThesis?.file_path);
                    const canViewFullPdf = hasPdf && Boolean(currentUser);
                    const canDownload = hasPdf && Boolean(currentUser) && ['admin', 'staff'].includes(currentUser.role);
                    const canSeePdfUnavailable = !hasPdf && Boolean(currentUser) && ['admin', 'staff'].includes(currentUser.role);

                    const handleTogglePdf = () => {
                        if (!canViewFullPdf) return;
                        setViewPdf(!viewPdf);
                    };

                    return (
                <div style={modalOverlay} onClick={() => { 
                    setSelectedThesis(null); 
                    setViewPdf(false); 
                }}>
                    <div style={{ ...modalContent, width: viewPdf ? '95%' : '550px', height: viewPdf ? '90vh' : undefined }} onClick={(e) => e.stopPropagation()}>
                        <h3 style={{ margin: '0 0 5px 0', color: '#333' }}>{selectedThesis.title}</h3>
                        
                        {/* CITATION */}
                        <p style={{ ...citationStyle, marginBottom: '15px' }}>{formatCitation(selectedThesis)}</p>

                        {!viewPdf ? (
                            <div style={{ flex: 1, overflowY: 'auto' }}>
                                {canSeePdfUnavailable && (
                                    <div style={{ marginBottom: '12px', padding: '10px', borderRadius: '6px', background: '#fff3cd', border: '1px solid #ffeeba', color: '#856404', fontSize: '0.9rem' }}>
                                        PDF file unavailable for this thesis.
                                    </div>
                                )}
                                {(() => {
                                    const deptObj = departments.find(d => d.dept_name === selectedThesis.department);
                                    let degreeName = '';
                                    let majorName = '';
                                    if (deptObj?.degrees?.length) {
                                        if (selectedThesis.degree_id) {
                                            const degreeObj = deptObj.degrees.find(d => d.degree_id === selectedThesis.degree_id);
                                            degreeName = degreeObj?.degree_name || '';
                                            if (selectedThesis.major_id) {
                                                majorName = degreeObj?.majors?.find(m => m.major_id === selectedThesis.major_id)?.major_name || '';
                                            }
                                        } else if (selectedThesis.major_id) {
                                            for (const degree of deptObj.degrees) {
                                                const foundMajor = degree.majors?.find(m => m.major_id === selectedThesis.major_id);
                                                if (foundMajor) {
                                                    degreeName = degree.degree_name;
                                                    majorName = foundMajor.major_name;
                                                    break;
                                                }
                                            }
                                        }
                                    }
                                    const deptDisplay = [selectedThesis.department, degreeName, majorName || major].filter(Boolean).join(' - ');
                                    return <p><strong>{selectedThesis.year}</strong> &middot; {deptDisplay}</p>;
                                })()}

                                {selectedThesis.authors && selectedThesis.authors.length > 0 && (
                                    <p style={{ margin: '6px 0', color: '#333' }}>
                                        <strong>Authors:</strong> {formatAuthorsForCitation(selectedThesis.authors)}
                                    </p>
                                )}

                                {selectedThesis.keywords && selectedThesis.keywords.length > 0 && (
                                    <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', marginBottom: '10px' }}>
                                        {selectedThesis.keywords.map((k, i) => (
                                            <span key={i} style={{ background: '#eef', padding: '6px 8px', borderRadius: '12px', fontSize: '13px' }}>
                                                {k}
                                            </span>
                                        ))}
                                    </div>
                                )}

                                <h5 style={{ marginTop: '15px', marginBottom: '8px', color: '#333' }}>Abstract</h5>
                                <div style={abstractBox}>{selectedThesis.abstract}</div>
                            </div>
                        ) : canViewFullPdf ? (
                            <div style={{ flex: 1, background: '#525659', borderRadius: '8px', overflow: 'hidden', height: '85vh' }}>
                                <iframe title="PDF" src={`${selectedFileUrl}#toolbar=0`} width="100%" height="100%" style={{ border: 'none' }} />
                            </div>
                        ) : (
                            <div style={restrictedOverlay} className="restricted-overlay">
                                <h2>Full PDF Access Restricted</h2>
                                <p>Only logged-in staff and admins can view the complete document.</p>
                                <p style={{ fontSize: '0.9rem', marginTop: '15px' }}>Request access to this thesis to receive the full PDF via email.</p>
                            </div>
                        )}

                        <div style={{ display: 'flex', gap: '10px', marginTop: '20px' }}>
                            {currentUser ? (
                                <button
                                    onClick={handleTogglePdf}
                                    style={{ ...readBtn, opacity: canViewFullPdf ? 1 : 0.5, cursor: canViewFullPdf ? 'pointer' : 'not-allowed' }}
                                    disabled={!canViewFullPdf}
                                    title={canViewFullPdf ? '' : (hasPdf ? 'Only staff and admins can view the full PDF.' : 'PDF file unavailable.')}
                                >
                                    {viewPdf ? 'Show Abstract' : 'Read Full PDF'}
                                </button>
                            ) : (
                                <>
                                    <button
                                        onClick={() => { setRequestType('author'); setShowRequestForm(true); }}
                                        disabled={!canRequestAuthors}
                                        style={{
                                            ...requestBtn,
                                            background: canRequestAuthors ? '#007bff' : '#6c757d',
                                            opacity: canRequestAuthors ? 1 : 0.6,
                                            cursor: canRequestAuthors ? 'pointer' : 'not-allowed'
                                        }}
                                        title={canRequestAuthors ? '' : 'No author emails are available for this thesis.'}
                                    >
                                        Request PDF file from Author/s
                                    </button>
                                    <button onClick={() => { setRequestType('admin'); setShowRequestForm(true); }} style={requestBtn}>
                                        Request PDF file from Admin/s
                                    </button>
                                </>
                            )}

                            {canDownload && (
                                <button onClick={() => handleDownload(selectedThesis.file_path)} style={downloadBtn}>
                                    Download
                                </button>
                            )}

                            <button onClick={() => { 
                                setSelectedThesis(null); 
                                setViewPdf(false); 
                            }} style={closeBtn}>
                                Close
                            </button>
                        </div>
                    </div>
                </div>
                    );
                })()
            )}

            {showGeneralRequestForm && (
                <div style={modalOverlay} onClick={() => setShowGeneralRequestForm(false)}>
                    <div style={{ ...modalContent, width: '550px' }} onClick={(e) => e.stopPropagation()}>
                        {isSubmitting && (
                            <div style={loaderInnerOverlay}>
                                <div style={spinnerStyle}></div>
                                <p style={{ fontWeight: 'bold', marginTop: '10px' }}>Submitting Request...</p>
                            </div>
                        )}

                        <h3 style={{ marginTop: 0 }}>Request a Thesis</h3>
                        <p style={{ color: '#666', fontSize: '0.9rem' }}>
                            Request a thesis project's full PDF file by filling out the form below. You can optionally select a specific thesis to request from its authors. If no thesis is selected, your request will be treated as a general inquiry and our staff will assist you in finding relevant theses.
                        </p>

                        <div style={thesisPickerStyle}>
                            <button
                                type="button"
                                onClick={() => setRequestPickerOpen(!requestPickerOpen)}
                                style={thesisPickerToggleStyle}
                            >
                                <span style={{ fontWeight: 'bold' }}>Select a Thesis (Optional)</span>
                                <span style={{ color: '#666', fontSize: '0.8rem' }}>
                                    {requestPickerOpen ? 'Hide filters' : 'Filter, search, then choose'}
                                </span>
                            </button>
                            {requestPickerOpen && (
                                <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap', marginTop: '10px' }}>
                                    <select
                                        style={{ ...inputStyle, flex: '1 1 180px' }}
                                        value={requestDeptFilter}
                                        onChange={(e) => setRequestDeptFilter(e.target.value)}
                                    >
                                        <option value="">All Departments</option>
                                        {departments.map(d => (
                                            <option key={d.dept_id} value={d.dept_name}>{d.dept_name}</option>
                                        ))}
                                    </select>
                                    {majorsForRequestDept.length > 0 && (
                                        <select
                                            style={{ ...inputStyle, flex: '1 1 180px' }}
                                            value={requestMajorId}
                                            onChange={(e) => setRequestMajorId(e.target.value)}
                                        >
                                            <option value="">All Majors</option>
                                            {majorsForRequestDept.map(m => (
                                                <option key={m.major_id} value={m.major_id}>{m.major_name}</option>
                                            ))}
                                        </select>
                                    )}
                                    <input
                                        type="text"
                                        placeholder="Search title..."
                                        value={requestSearch}
                                        onChange={(e) => setRequestSearch(e.target.value)}
                                        style={{ ...inputStyle, flex: '1 1 220px' }}
                                    />
                                    <select
                                        style={{ ...inputStyle, flex: '2 1 260px' }}
                                        value={requestThesisId}
                                        onChange={(e) => setRequestThesisId(e.target.value)}
                                    >
                                        <option value="">No specific thesis (General Request)</option>
                                        {(theses || [])
                                            .filter(t => !requestDeptFilter || t.department === requestDeptFilter)
                                            .filter(t => !requestMajorId || String(t.major_id || '') === String(requestMajorId))
                                            .filter(t => !requestSearch || String(t.title || '').toLowerCase().includes(requestSearch.toLowerCase()))
                                            .sort((a, b) => String(a.title || '').localeCompare(String(b.title || '')))
                                            .map(t => (
                                                <option key={t.thesis_id} value={t.thesis_id}>
                                                    {t.title} ({t.year || 'N/A'}){t.file_path ? '' : ' - PDF file unavailable'}
                                                </option>
                                            ))}
                                    </select>
                                </div>
                            )}
                        </div>

                        <input
                            placeholder="Full Name"
                            style={{ ...inputStyle, width: '100%', marginBottom: '10px' }}
                            value={requestData.name}
                            onChange={e => setRequestData({ ...requestData, name: e.target.value })}
                        />
                        <input
                            placeholder="Email Address"
                            style={{ ...inputStyle, width: '100%', marginBottom: '10px' }}
                            value={requestData.email}
                            onChange={e => setRequestData({ ...requestData, email: e.target.value })}
                        />
                        <textarea
                            placeholder="Purpose of request (e.g., Undergraduate Research)..."
                            style={{ ...inputStyle, width: '100%', height: '100px', marginBottom: '10px', resize: 'none' }}
                            value={requestData.message}
                            onChange={e => setRequestData({ ...requestData, message: e.target.value })}
                        />

                        <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
                            <button
                                onClick={submitGeneralRequest}
                                disabled={isSubmitting}
                                style={{ ...btnStyle, flex: 1, opacity: isSubmitting ? 0.7 : 1 }}
                            >
                                {isSubmitting ? 'Sending...' : 'Admin Request'}
                            </button>
                            <button
                                onClick={submitGeneralAuthorRequest}
                                disabled={isSubmitting || !requestThesisId || !canAuthorRequest}
                                style={{
                                    ...btnStyle,
                                    flex: 1,
                                    background: (!requestThesisId || !canAuthorRequest) ? '#6c757d' : '#007bff',
                                    opacity: (isSubmitting || !requestThesisId || !canAuthorRequest) ? 0.6 : 1,
                                    cursor: (isSubmitting || !requestThesisId || !canAuthorRequest) ? 'not-allowed' : 'pointer'
                                }}
                                title={
                                    !requestThesisId
                                        ? 'Select a specific thesis to request from authors.'
                                        : (!canAuthorRequest ? 'This thesis has no author emails on file.' : '')
                                }
                            >
                                {isSubmitting ? 'Sending...' : 'Author Request'}
                            </button>
                            <button
                                onClick={() => { setShowGeneralRequestForm(false); }}
                                style={closeBtn}
                            >
                                Cancel
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* REQUEST FORM MODAL */}
            {showRequestForm && selectedThesis && (
                <div style={modalOverlay} onClick={() => setShowRequestForm(false)}>
                    <div style={{ ...modalContent, width: '550px' }} onClick={(e) => e.stopPropagation()}>
                        {/* INTERNAL LOADING OVERLAY */}
                        {isSubmitting && (
                            <div style={loaderInnerOverlay}>
                                <div style={spinnerStyle}></div>
                                <p style={{ fontWeight: 'bold', marginTop: '10px' }}>Submitting Request...</p>
                            </div>
                        )}

                        <h3 style={{ marginTop: 0 }}>
                            {requestType === 'author' 
                                ? 'Request PDF from Authors' 
                                : 'Request PDF from Administration'}
                        </h3>
                        <p style={{ color: '#666', fontSize: '0.9rem', marginBottom: '15px' }}>
                            Requesting: <strong>{selectedThesis.title}</strong>
                        </p>

                        {requestType === 'author' && (
                            <p style={{ background: '#f0f7ff', padding: '10px', borderRadius: '4px', fontSize: '0.85rem', color: '#004085', marginBottom: '15px', borderLeft: '4px solid #0066cc' }}>
                                📧 Your request will be sent directly to the thesis author(s). They will review your request and may approve it to send you the full PDF file.
                            </p>
                        )}

                        {requestType === 'admin' && (
                            <p style={{ background: '#fff3cd', padding: '10px', borderRadius: '4px', fontSize: '0.85rem', color: '#856404', marginBottom: '15px', borderLeft: '4px solid #ffc107' }}>
                                📋 Your request will be submitted to our administration team. They will review and respond to you via email.
                            </p>
                        )}
                        
                        <input 
                            placeholder="Full Name" 
                            style={{ ...inputStyle, width: '100%', marginBottom: '10px' }} 
                            value={requestData.name} 
                            onChange={e => setRequestData({...requestData, name: e.target.value})} 
                        />
                        <input 
                            placeholder="Email Address" 
                            style={{ ...inputStyle, width: '100%', marginBottom: '10px' }} 
                            value={requestData.email} 
                            onChange={e => setRequestData({...requestData, email: e.target.value})} 
                        />
                        <textarea 
                            placeholder={requestType === 'author' 
                                ? 'Message for author (optional)...' 
                                : 'Why do you need this thesis? (e.g., Research, Citation, etc.)...'}
                            style={{ ...inputStyle, width: '100%', height: '100px', marginBottom: '10px', resize: 'none' }} 
                            value={requestData.message} 
                            onChange={e => setRequestData({...requestData, message: e.target.value})} 
                        />
                        
                        <div style={{display:'flex', gap:'10px'}}>
                            <button 
                                onClick={requestType === 'author' ? submitAuthorRequest : submitRequest} 
                                disabled={isSubmitting} 
                                style={{ ...btnStyle, flex: 1, opacity: isSubmitting ? 0.7 : 1 }}
                            >
                                {isSubmitting ? 'Sending...' : 'Submit Request'}
                            </button>
                            <button 
                                onClick={() => { 
                                    setShowRequestForm(false);
                                    setRequestType('admin');
                                }} 
                                style={closeBtn}
                            >
                                Cancel
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

// --- STYLES ---
const backBtn = { padding: '10px 15px', background: '#6c757d', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer' };
const filterBarStyle = { display: 'flex', gap: '10px', background: '#f8f9fa', padding: '15px', borderRadius: '8px', marginBottom: '20px', flexWrap: 'wrap' };
const inputStyle = { padding: '10px', borderRadius: '4px', border: '1px solid #ddd', fontSize: '0.9rem', boxSizing: 'border-box' };
const cardStyle = { border: '1px solid #eee', padding: '22px 24px', borderRadius: '14px', boxShadow: '0 6px 10px rgba(0,0,0,0.06)', background: '#fff', display: 'flex', flexDirection: 'column' };
const citationStyle = { fontSize: '0.95rem', color: '#666', fontStyle: 'italic', margin: '0 0 14px 0', lineHeight: '1.5' };
const thumbnailWrapperStyle = { marginBottom: '12px', border: '1px solid #ddd', borderRadius: '6px', overflow: 'hidden', background: '#fafafa', minHeight: '150px', display: 'flex', alignItems: 'center', justifyContent: 'center' };
const thumbnailLoadingStyle = { display: 'flex', alignItems: 'center', justifyContent: 'center', width: '100%', height: '150px', background: '#f5f5f5', color: '#999', fontWeight: '500' };
const thumbnailErrorStyle = { display: 'flex', alignItems: 'center', justifyContent: 'center', width: '100%', height: '150px', background: '#fff5f5', color: '#c00', fontWeight: '500' };
const viewMoreBtn = { border: '1px solid #800000', background: '#800000', color: '#fff', padding: '10px 16px', cursor: 'pointer', fontWeight: 'bold', marginTop: 'auto', textAlign: 'left', borderRadius: '6px', width: 'fit-content' };
const sectionDividerStyle = { height: '1px', background: '#ececec', margin: '6px 0 12px 0' };
const modalOverlay = { position: 'fixed', top: 0, left: 0, width: '100%', height: '100%', background: 'rgba(0,0,0,0.7)', display: 'flex', justifyContent: 'center', alignItems: 'center', zIndex: 1000 };
const modalContent = { background: 'white', padding: '20px', borderRadius: '12px', maxWidth: '95%', maxHeight: '80vh', display: 'flex', flexDirection: 'column', boxShadow: '0 20px 40px rgba(0,0,0,0.4)', overflowY: 'auto', boxSizing: 'border-box' };
const abstractBox = { background: '#f9f9f9', padding: '15px', borderRadius: '6px', fontSize: '0.95rem', lineHeight: '1.6', maxHeight: '400px', overflowY: 'auto', border: '1px solid #eee' };
const readBtn = { flex: 1, padding: '12px', background: '#800000', color: 'white', borderRadius: '4px', border: 'none', cursor: 'pointer', fontWeight: 'bold' };
const downloadBtn = { flex: 1, padding: '12px', background: '#28a745', color: 'white', borderRadius: '4px', border: 'none', cursor: 'pointer', fontWeight: 'bold' };
const requestBtn = { flex: 1, padding: '12px', background: '#007bff', color: 'white', borderRadius: '4px', border: 'none', cursor: 'pointer', fontWeight: 'bold' };
const closeBtn = { padding: '12px 25px', border: '1px solid #ddd', background: 'white', borderRadius: '4px', cursor: 'pointer' };
const restrictedOverlay = { height: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', color: 'white', padding: '40px', textAlign: 'center', background: '#333' };
const btnStyle = { padding: '10px 20px', background: '#28a745', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer' };
const bannerStyle = { background: '#f0f7ff', padding: '16px 20px', borderRadius: '12px', border: '1px solid #cce5ff', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '15px' };
const thesisPickerStyle = { background: '#f8f9fa', border: '1px solid #e5e7eb', borderRadius: '8px', padding: '12px', marginBottom: '10px' };
const thesisPickerToggleStyle = { width: '100%', display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: 'transparent', border: 'none', padding: 0, cursor: 'pointer' };
const loaderInnerOverlay = { position: 'absolute', inset: 0, background: 'rgba(255,255,255,0.9)', zIndex: 10, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', borderRadius: '12px' };
const spinnerStyle = { border: '4px solid #f3f3f3', borderTop: '4px solid #800000', borderRadius: '50%', width: '40px', height: '40px', animation: 'spin 1s linear infinite' };

export default ThesisDetailView;

